import React, { useState, useEffect, useRef } from 'react';
import { Box, Spinner } from '@amzn/awsui-components-react';
import mammoth from 'mammoth';

/**
 * EmbedWord - Renders a .docx file in an iframe (same pattern as PDF).
 * Fetches the file, converts to HTML via mammoth, renders in iframe via blob URL.
 *
 * Props:
 *   fileUrl (string)  - presigned S3 URL to the .docx file
 *   fileName (string) - original file name (for display)
 */
const EmbedWord = ({ fileUrl, fileName = 'document.docx' }) => {
  const [iframeSrc, setIframeSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!fileUrl) return;
    loadWordFile(fileUrl);
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileUrl]);

  const loadWordFile = async (url) => {
    setLoading(true);
    setErrorMsg(null);
    setIframeSrc(null);
    // Revoke previous blob URL if exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });

      // Wrap in a full HTML document for proper iframe rendering
      const htmlDoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; line-height: 1.6; color: #333; }
    table { border-collapse: collapse; width: 100%; margin: 12px 0; }
    td, th { border: 1px solid #ddd; padding: 8px; }
    img { max-width: 100%; }
  </style>
</head>
<body>${result.value}</body>
</html>`;

      const blob = new Blob([htmlDoc], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      setIframeSrc(blobUrl);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to load Word document');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box padding="l"><Spinner /> Loading Word document...</Box>;
  }

  if (errorMsg) {
    return <Box padding="l" color="text-status-error">{errorMsg}</Box>;
  }

  if (!iframeSrc) {
    return null;
  }

  return (
    <iframe
      sandbox=""
      className="fixed_embedment"
      src={iframeSrc}
      style={{ width: '100%', height: '800px', border: 'none' }}
      title={fileName}
    />
  );
};

export default EmbedWord;
